/*
 * Copyright 2026 Sony Corporation
 */

const CACHE = 'asset-bytes-v1';

/** Absent in jsdom, and in any context the browser does not consider secure. */
const open = async (): Promise<Cache | undefined> =>
  typeof caches === 'undefined' ? undefined : caches.open(CACHE).catch(() => undefined);

/**
 * Bytes kept across reloads, under a key that names the asset.
 *
 * The key is given rather than taken from the response because the URL a model
 * arrives on is often signed: a different string every time, which as a cache
 * key would only ever record misses. What identifies the bytes is the asset,
 * so `download` may go anywhere as long as the key does not move.
 *
 * The store is an optimisation and never a requirement — a browser that
 * refuses to open it, or one already at quota, still gets the download.
 */
export const cachedBlob = async (key: string, download: () => Promise<Blob>): Promise<Blob> => {
  const store = await open();
  const hit = await store?.match(key).catch(() => undefined);
  if (hit) {
    return hit.blob();
  }

  const blob = await download();
  await store?.put(key, new Response(blob)).catch(() => undefined);
  return blob;
};

/** For a URL that names its content, which is its own key. */
export const fetchCached = async (url: string, signal?: AbortSignal): Promise<Blob> =>
  cachedBlob(url, async () => {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`${url} could not be downloaded (${String(response.status)}).`);
    }
    return response.blob();
  });
